/**
 * Mixpanel Provider Component
 * Provides Mixpanel context to the entire application
 */

import React, { createContext, useContext, useEffect, useRef } from 'react';
import mixpanelService from './mixpanel.service';
import type { AppEnvironment } from './types';

interface MixpanelContextValue {
  isInitialized: boolean;
}

const MixpanelContext = createContext<MixpanelContextValue>({
  isInitialized: false,
});

export const useMixpanelContext = () => useContext(MixpanelContext);

interface MixpanelProviderProps {
  children: React.ReactNode;
  token?: string;
  environment?: AppEnvironment;
  debug?: boolean;
}

export const MixpanelProvider: React.FC<MixpanelProviderProps> = ({
  children,
  token,
  environment = 'development',
  debug = false,
}) => {
  const isInitializedRef = useRef(false);

  useEffect(() => {
    // Prevent double initialization in StrictMode
    if (isInitializedRef.current) {
      return;
    }

    // Get token from props or environment variables
    const mixpanelToken = token || import.meta.env.VITE_MIXPANEL_TOKEN;

    if (!mixpanelToken) {
      console.warn('[MixpanelProvider] No token provided. Analytics disabled.');
      return;
    }

    // Determine environment
    const appEnv =
      environment ||
      (import.meta.env.VITE_APP_ENV as AppEnvironment) ||
      'development';

    // Initialize Mixpanel
    mixpanelService.init({
      token: mixpanelToken,
      debug: debug || appEnv === 'development',
      track_pageview: false, // We'll track manually
      persistence: 'localStorage',
      secure_cookie: appEnv === 'production',
      ip: false, // Privacy-friendly
    });

    // Set environment
    mixpanelService.setEnvironment(appEnv);

    // Register global super properties
    mixpanelService.registerSuperProperties({
      app_name: 'FacturaLibre',
      app_version: '2.0.0',
      environment: appEnv,
    });

    isInitializedRef.current = true;

    console.log('[MixpanelProvider] Initialized with environment:', appEnv);
  }, [token, environment, debug]);

  const contextValue: MixpanelContextValue = {
    isInitialized: mixpanelService.isInitialized(),
  };

  return (
    <MixpanelContext.Provider value={contextValue}>
      {children}
    </MixpanelContext.Provider>
  );
};

export default MixpanelProvider;
